import { LoadingButton } from "@mui/lab";
import { Box, DialogTitle, FormControlLabel, Switch, TextField } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import {
	DunningLevel,
	useCreateDunningLevelMutation,
	useUpdateDunningLevelMutation,
} from "@repo/server-api";
import { BoundEuroField, useNotification } from "@repo/shared/components";
import { useTranslation } from "next-i18next";
import { forwardRef, useImperativeHandle, useState } from "react";

type DunningLevelFe = {
	id: string | null;
	level: number;
	name: string;
	paymentDays: number;
	feeAmount: number;
	feeCurrency: string;
	isActive: boolean;
};

const defaultForm = (level: number = 1): DunningLevelFe => ({
	id: null,
	level: level,
	name: "",
	paymentDays: 14,
	feeAmount: 0,
	feeCurrency: "EUR",
	isActive: true,
});

export type DunningLevelDialogHandle = {
	show: (level: DunningLevel | null) => void;
};

export const DialogDunningLevel = forwardRef<
	DunningLevelDialogHandle,
	{ contractorId: string; level: number; onSaved: () => void }
>((props, ref) => {
	const { t } = useTranslation();
	const { showNotification } = useNotification();

	const [open, setOpen] = useState(false);
	const [form, setForm] = useState<DunningLevelFe>(defaultForm(props.level));
	const [saveClicked, setSaveClicked] = useState(false);
	const [errors, setErrors] = useState<Partial<Record<keyof DunningLevelFe, boolean>>>({});

	useImperativeHandle(ref, () => ({
		show: (level: DunningLevel | null) => {
			if (level) {
				setForm({
					id: level.id,
					level: level.level,
					name: level.name,
					paymentDays: level.paymentDays,
					feeAmount: level.feeAmount,
					feeCurrency: level.feeCurrency,
					isActive: level.isActive,
				});
			} else {
				setForm(defaultForm(props.level));
			}
			setErrors({});
			setOpen(true);
		},
	}));

	const [createDunningLevel] = useCreateDunningLevelMutation({
		onCompleted(data) {
			if (data.createDunningLevel.dunningLevel) {
				props.onSaved();
				handleClose();
				showNotification(t("contractors.dunning.created"), "success");
			}
			data.createDunningLevel.errors?.forEach((error) => {
				if ("message" in error) showNotification(error.message, "error");
			});
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const [updateDunningLevel] = useUpdateDunningLevelMutation({
		onCompleted(data) {
			if (data.updateDunningLevel.dunningLevel) {
				props.onSaved();
				handleClose();
				showNotification(t("contractors.dunning.updated"), "success");
			}
			data.updateDunningLevel.errors?.forEach((error) => {
				if ("message" in error) showNotification(error.message, "error");
			});
			setSaveClicked(false);
		},
		onError(error) {
			showNotification(error.message, "error");
			setSaveClicked(false);
		},
	});

	const handleClose = () => {
		setOpen(false);
		setForm(defaultForm());
		setErrors({});
		setSaveClicked(false);
	};

	const validate = (): boolean => {
		const newErrors: Partial<Record<keyof DunningLevelFe, boolean>> = {};
		if (!form.name.trim()) newErrors.name = true;
		if (!form.feeCurrency.trim()) newErrors.feeCurrency = true;
		if (form.level < 1) newErrors.level = true;
		if (form.paymentDays < 1) newErrors.paymentDays = true;
		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = () => {
		if (!validate()) return;
		setSaveClicked(true);
		if (form.id) {
			updateDunningLevel({
				variables: {
					id: form.id,
					feeAmount: form.feeAmount,
					feeCurrency: form.feeCurrency,
					isActive: form.isActive,
					name: form.name,
					paymentDays: form.paymentDays,
				},
			});
		} else {
			createDunningLevel({
				variables: {
					contractorId: props.contractorId,
					feeAmount: form.feeAmount,
					feeCurrency: form.feeCurrency,
					isActive: form.isActive,
					name: form.name,
					paymentDays: form.paymentDays,
					level: form.level,
				},
			});
		}
	};

	const isEdit = !!form.id;

	return (
		<Dialog
			open={open}
			data-testid="dunning-level-dialog"
			aria-describedby="dunning-level-dialog-description"
			maxWidth="sm"
			fullWidth>
			<DialogTitle>
				{isEdit ? t("contractors.dunning.edit") : t("contractors.dunning.create")}
			</DialogTitle>
			<DialogContent className="flex-col gap-4">
				<Box sx={{ mt: 1 }} className="fullCenter">
					<TextField
						data-testid="dunning-name-input"
						label={t("name")}
						className="w-100"
						value={form.name}
						onChange={(e) => {
							setForm({ ...form, name: e.target.value });
							setErrors({ ...errors, name: false });
						}}
						error={errors.name}
						helperText={errors.name ? t("required", { ns: "validation" }) : ""}
					/>
				</Box>
				{!isEdit && (
					<Box className="fullCenter">
						<TextField
							data-testid="dunning-level-input"
							label={t("level")}
							type="number"
							className="w-100"
							value={form.level}
							onChange={(e) => {
								setForm({ ...form, level: parseInt(e.target.value) || 1 });
								setErrors({ ...errors, level: false });
							}}
							error={errors.level}
							helperText={errors.level ? t("required", { ns: "validation" }) : ""}
							disabled
						/>
					</Box>
				)}
				<Box className="fullCenter">
					<TextField
						data-testid="dunning-payment-days-input"
						label={t("payment-days")}
						type="number"
						className="w-100"
						value={form.paymentDays}
						onChange={(e) => {
							setForm({ ...form, paymentDays: parseInt(e.target.value) || 0 });
							setErrors({ ...errors, paymentDays: false });
						}}
						error={errors.paymentDays}
						helperText={errors.paymentDays ? t("required", { ns: "validation" }) : ""}
					/>
				</Box>
				<Box className="fullCenter">
					<BoundEuroField
						label={t("fee-amount")}
						allowNegative={false}
						decimalSeparator=","
						thousandSeparator="."
						boundedValue={{
							value: form.feeAmount,
							onChangeValue: (v) => setForm({ ...form, feeAmount: v ?? 0 }),
							errorKey: "feeAmount",
						}}
						additionalProps={{ className: "w-100" }}
					/>
				</Box>
			</DialogContent>
			<DialogActions>
				<Button data-testid="dunning-cancel-button" onClick={handleClose} autoFocus>
					{t("cancel")}
				</Button>
				<LoadingButton
					data-testid="dunning-save-button"
					loading={saveClicked}
					onClick={handleSubmit}
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
});
